// shared/interceptors/http-toast.interceptor.ts
import { HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, throwError } from "rxjs";
import { ToastService } from "@shared/services/toast/toast.service";
import { SKIP_ERROR_TOAST } from "./skip-error-toast.token";

export const httpToastInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);

  return next(req).pipe(
    catchError((error) => {
      // Requête « best-effort » : ne pas afficher de toast d'erreur.
      if (req.context.get(SKIP_ERROR_TOAST)) {
        return throwError(() => error);
      }

      // Le 401 est géré ailleurs : session expirée -> sessionExpiredInterceptor
      // (toast "session expirée" + déconnexion) ; échec de login -> message inline
      // de la page de connexion. On évite ici un toast 401 en double.
      if (error.status === 401) {
        return throwError(() => error);
      }

      let errorMessage = "Une erreur est survenue";

      if (error.error instanceof ErrorEvent) {
        // Erreur côté client
        errorMessage = error.error.message;
      } else if (error.status >= 200 && error.status < 300) {
        // Réponse 2xx avec corps non-JSON (ex: 201 vide) — pas une vraie erreur
        return throwError(() => error);
      } else {
        // Erreur côté serveur
        const status = error.status;

        switch (status) {
          case 0:
            errorMessage = "Erreur de connexion au serveur";
            break;
          case 400:
            errorMessage = error.error?.message || "Requête incorrecte";
            break;
          case 401:
            errorMessage = "Non autorisé. Veuillez vous connecter.";
            break;
          case 403:
            errorMessage = "Accès interdit";
            break;
          case 404:
            errorMessage = "Ressource non trouvée";
            break;
          case 422:
            errorMessage = error.error?.message || "Données invalides";
            break;
          case 409:
            errorMessage = error.error?.message || "Conflit de données";
            break;
          case 500:
            errorMessage = error.error?.message || "Erreur interne du serveur";
            break;
          case 503:
            errorMessage = "Service temporairement indisponible";
            break;
          default:
            errorMessage = error.error?.message || `Erreur ${status}`;
        }
      }

      // Afficher le toast d'erreur
      toastService.error(errorMessage);

      return throwError(() => error);
    })
  );
};
